import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

/**
 * @description Instancia de Axios pre-configurada con la URL base de la API.
 * @type {import('axios').AxiosInstance}
 */
const api = axios.create({
  baseURL: API_URL,
});

/**
 * @description Interceptor de peticiones de Axios.
 * Se ejecuta antes de cada petición para inyectar el token de autenticación JWT en las cabeceras
 * si un usuario ha iniciado sesión.
 */
api.interceptors.request.use((config) => {
  const user = JSON.parse(localStorage.getItem('user'));
  if (user && user.accessToken) {
    config.headers['Authorization'] = `Bearer ${user.accessToken}`;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      logout();
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

/**
 * @description Realiza la petición de login a la API.
 * @param {string} email El email del usuario.
 * @param {string} password La contraseña del usuario.
 * @returns {Promise<object>} Los datos del usuario, incluyendo el `accessToken`.
 * @throws {object} El error devuelto por la API si las credenciales son incorrectas.
 */
const login = async (email, password) => {
  try {
    const response = await axios.post(`${API_URL}/auth/login`, {
      email,
      password,
    });
    if (response.data.accessToken) {
      // Almacena los datos del usuario en el localStorage para mantener la sesión.
      localStorage.setItem('user', JSON.stringify(response.data));
    }
    return response.data;
  } catch (error) {
    throw error.response.data;
  }
};

/**
 * @description Registra un nuevo usuario técnico en el sistema.
 * El rol se fija en TECHNICIAN — los roles ADMIN y SUPERVISOR se asignan
 * directamente desde la administración, nunca desde el formulario público.
 * @param {object} userData Datos del nuevo usuario.
 * @param {string} userData.username Nombre completo del técnico.
 * @param {string} userData.phone Teléfono de contacto.
 * @param {string} userData.email Correo electrónico.
 * @param {string} userData.password Contraseña en texto plano (el backend la hashea).
 * @returns {Promise<object>} El usuario creado devuelto por la API.
 * @throws {object} El error devuelto por la API (p.ej. email o usuario duplicado).
 */
const register = async ({ username, phone, email, password, role }) => {
  try {
    // Usa la instancia `api` (con interceptor) — el endpoint ahora requiere JWT con rol ADMIN
    const response = await api.post('/auth/register', {
      username,
      phone,
      email,
      password,
      role: role ?? 'TECHNICIAN',
    });
    return response.data;
  } catch (error) {
    throw error.response?.data ?? { message: 'Error al conectar con el servidor.' };
  }
};

/**
 * @description Cierra la sesión del usuario eliminando sus datos del localStorage.
 */
const logout = () => {
  localStorage.removeItem('user');
};

/**
 * @description Decodifica el payload de un JWT sin verificar la firma.
 * Usado únicamente para leer claims públicos (role, email, sub) en el cliente.
 * @param {string} token - El token JWT.
 * @returns {object | null} El payload decodificado, o null si el token es inválido.
 */
const decodeJwtPayload = (token) => {
  try {
    const base64Payload = token.split('.')[1];
    const jsonPayload = atob(base64Payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

/**
 * @description Obtiene los datos del usuario actual almacenados en el localStorage.
 * Enriquece el objeto con los claims del JWT (role, email, user_id) para que
 * los componentes puedan usar currentUser.role sin depender de un endpoint adicional.
 * @returns {object | null} El objeto del usuario con claims del token, o `null` si no existe.
 */
const getCurrentUser = () => {
  const stored = JSON.parse(localStorage.getItem('user'));
  if (!stored || !stored.accessToken) return null;

  // Extraer claims del payload del JWT para exponer role, email y user_id
  const payload = decodeJwtPayload(stored.accessToken);
  if (!payload) return stored;

  return {
    ...stored,
    role: payload.role,
    email: payload.email,
    user_id: payload.sub,
  };
};

const authService = {
  login,
  register,
  logout,
  getCurrentUser,
};

export { api, login, register, logout, getCurrentUser }; // Exportamos la instancia de `api`
export default authService;